import React, { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus({ type: 'success', message: response.data.message });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'contact_form_submit',
        form_type: 'consultation',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit form. Please try calling us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-4 md:mb-6 tracking-tight">
            Contact Us
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            Ready to discuss your case? We offer free, no-obligation consultations. 
            Reach out today to get started.
          </p>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-slate-900 mb-6 md:mb-8 tracking-tight">
                Get In Touch
              </h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Phone</p>
                    <a
                      href="tel:+13363541040"
                      className="text-primary hover:underline text-lg"
                      data-testid="contact-phone"
                    >
                      (336) 354-1040
                    </a>
                    <p className="text-sm text-slate-600 mt-1">Click to call on mobile</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Email</p>
                    <a
                      href="mailto:intake@rizzilawgroup.com"
                      className="text-primary hover:underline"
                      data-testid="contact-email"
                    >
                      intake@rizzilawgroup.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Winston-Salem Office</p>
                    <p className="text-slate-600">
                      110 Oakwood Drive, Suite 450<br />
                      Winston-Salem, NC 27103
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Greenville Office</p>
                    <p className="text-slate-600">
                      New Address Coming Soon<br />
                      Greenville, SC
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Office Hours</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  <strong>Monday - Thursday:</strong> 8:00 AM - 4:30 PM<br />
                  <strong>Friday:</strong> 8:00 AM - 4:00 PM<br />
                  <strong>Saturday - Sunday:</strong> Closed<br />
                  <span className="text-xs mt-2 block">Emergency consultations available by appointment</span>
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-slate-900 mb-6 md:mb-8 tracking-tight">
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-slate-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    data-testid="contact-message"
                  ></textarea>
                </div>

                {submitStatus && (
                  <div
                    className={`p-4 rounded-sm ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}
                    data-testid="submit-status"
                  >
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-xs text-slate-500 leading-relaxed">
                  <strong>Important:</strong> Submitting this form does not create an attorney-client relationship. 
                  Do not include confidential or time-sensitive information. For urgent matters, please call us at 
                  (336) 354-1040.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Free Consultation CTA */}
      <section className="py-10 md:py-16 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold mb-4">
            Free Consultations Available
          </h2>
          <p className="text-lg text-white/90 leading-relaxed">
            We offer free, no-obligation consultations to discuss your case. 
            There is no risk in reaching out, and we're here to help.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;